import React, { useEffect, useState, useCallback } from 'react';
import { songAPI, labelAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import ExcelUploadModal from '../../components/ExcelUploadModal';
import { 
    Plus, 
    Search, 
    Music,
    FileSpreadsheet,
    Loader2,
    Disc3,
    Users,
    TrendingUp,
    X,
    Eye,
    ArrowLeft,
    Tag
} from 'lucide-react';

const ContentPage = () => {
    const [catalogSummary, setCatalogSummary] = useState(null);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [matchingLabels, setMatchingLabels] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchTab, setSearchTab] = useState('songs');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    
    // Label filter state
    const [selectedLabelFilter, setSelectedLabelFilter] = useState(null);
    const [labelSongs, setLabelSongs] = useState([]);
    const [loadingLabelSongs, setLoadingLabelSongs] = useState(false);

    // New song form state
    const [newSong, setNewSong] = useState({
        song_name: '',
        singer_name: '',
        lyricist_name: '',
        composer_name: '',
        music_producer: '',
        label_id: '',
        upc_code: '',
        isrc_code: '',
        audio_file: '',
        artwork_file: ''
    });

    const fetchData = useCallback(async () => {
        try {
            // Only fetch catalog summary initially - labels are fetched when needed
            const summaryRes = await songAPI.getCatalogSummary();
            setCatalogSummary(summaryRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load catalog');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch labels only when add dialog opens
    useEffect(() => {
        if (addDialogOpen && labels.length === 0) {
            labelAPI.getOptions().then(res => setLabels(res.data)).catch(console.error);
        }
    }, [addDialogOpen, labels.length]);

    const handleSearch = async (e) => {
        e?.preventDefault();
        if (!searchQuery.trim()) {
            setShowSearchResults(false);
            return;
        }

        setSearching(true);
        try {
            // Search both songs and labels
            const [songsRes, labelsRes] = await Promise.all([
                songAPI.search(searchQuery, null, 50),
                labelAPI.search(searchQuery, 20)
            ]);
            
            setSearchResults(songsRes.data.results || []);
            setMatchingLabels(labelsRes.data.results || []);
            setShowSearchResults(true);
            
            // Auto-switch to labels tab if only labels match
            if ((labelsRes.data.results?.length > 0) && (songsRes.data.results?.length === 0)) {
                setSearchTab('labels');
            } else {
                setSearchTab('songs');
            }
        } catch (error) {
            console.error('Search failed:', error);
            toast.error('Search failed');
        } finally {
            setSearching(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setMatchingLabels([]);
        setShowSearchResults(false);
    };

    const handleViewLabelSongs = async (label) => {
        setSelectedLabelFilter(label);
        setLoadingLabelSongs(true);
        setShowSearchResults(false);
        setSearchQuery('');
        
        try {
            const response = await songAPI.getAll(label.label_id);
            setLabelSongs(response.data || []);
        } catch (error) {
            console.error('Failed to fetch label songs:', error);
            toast.error('Failed to load songs for this label');
        } finally {
            setLoadingLabelSongs(false);
        }
    };

    const clearLabelFilter = () => {
        setSelectedLabelFilter(null);
        setLabelSongs([]);
    };

    const handleAddSong = async (e) => {
        e.preventDefault();
        try {
            await songAPI.create(newSong);
            toast.success('Song added successfully');
            setAddDialogOpen(false);
            setNewSong({
                song_name: '',
                singer_name: '',
                lyricist_name: '',
                composer_name: '',
                music_producer: '',
                label_id: '',
                upc_code: '',
                isrc_code: '',
                audio_file: '',
                artwork_file: ''
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add song');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // If viewing a specific label's songs
    if (selectedLabelFilter) {
        return (
            <div className="space-y-6" data-testid="content-page">
                {/* Header with back button */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={clearLabelFilter}
                            className="border-border/50"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Catalog
                        </Button>
                        <div>
                            <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
                                <Disc3 className="w-6 h-6 text-primary" />
                                {selectedLabelFilter.label_name}
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                {selectedLabelFilter.song_count} songs • {selectedLabelFilter.revenue_share}% revenue share
                            </p>
                        </div>
                    </div>
                </div>

                {/* Songs Table for Label */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Music className="w-5 h-5 text-primary" />
                            Songs ({labelSongs.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loadingLabelSongs ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            </div>
                        ) : (
                            <div className="rounded-lg border border-border/40 overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                                            <TableHead className="font-mono text-xs uppercase">Song Name</TableHead>
                                            <TableHead className="font-mono text-xs uppercase">Singer</TableHead>
                                            <TableHead className="font-mono text-xs uppercase">Lyricist</TableHead>
                                            <TableHead className="font-mono text-xs uppercase">Composer</TableHead>
                                            <TableHead className="font-mono text-xs uppercase">UPC</TableHead>
                                            <TableHead className="font-mono text-xs uppercase">ISRC</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {labelSongs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    No songs found for this label
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            labelSongs.map((song) => (
                                                <TableRow key={song.id} className="hover:bg-muted/10 border-border/40">
                                                    <TableCell className="font-medium">{song.song_name}</TableCell>
                                                    <TableCell className="text-muted-foreground">{song.singer_name || '-'}</TableCell>
                                                    <TableCell className="text-muted-foreground">{song.lyricist_name || '-'}</TableCell>
                                                    <TableCell className="text-muted-foreground">{song.composer_name || '-'}</TableCell>
                                                    <TableCell className="font-mono text-sm text-muted-foreground">{song.upc_code || '-'}</TableCell>
                                                    <TableCell className="font-mono text-sm text-muted-foreground">{song.isrc_code || '-'}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="content-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Content Management</h1>
                    <p className="text-muted-foreground">Catalog overview and song metadata</p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        className="border-border/50"
                        data-testid="upload-excel-btn"
                        onClick={() => setUploadDialogOpen(true)}
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Upload Excel
                    </Button>
                    <ExcelUploadModal
                        kind="catalogue"
                        validateUrl="/songs/validate"
                        uploadUrl="/songs/upload"
                        open={uploadDialogOpen}
                        onOpenChange={setUploadDialogOpen}
                        onUploaded={(data) => {
                            toast.success(`${data.records_created || 0} songs created${data.labels_created ? `, ${data.labels_created} new labels` : ''}`);
                            fetchData();
                        }}
                    />

                    <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-primary hover:bg-primary/90" data-testid="add-song-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Song
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-card border-border/40 max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading">Add New Song</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddSong} className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Song Name *</Label>
                                        <Input
                                            value={newSong.song_name}
                                            onChange={(e) => setNewSong({...newSong, song_name: e.target.value})}
                                            required
                                            className="bg-background/50"
                                            data-testid="song-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Singer Name</Label>
                                        <Input
                                            value={newSong.singer_name}
                                            onChange={(e) => setNewSong({...newSong, singer_name: e.target.value})}
                                            className="bg-background/50"
                                            data-testid="singer-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Lyricist</Label>
                                        <Input
                                            value={newSong.lyricist_name}
                                            onChange={(e) => setNewSong({...newSong, lyricist_name: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Composer</Label>
                                        <Input
                                            value={newSong.composer_name}
                                            onChange={(e) => setNewSong({...newSong, composer_name: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Music Producer</Label>
                                        <Input
                                            value={newSong.music_producer}
                                            onChange={(e) => setNewSong({...newSong, music_producer: e.target.value})}
                                            className="bg-background/50"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Label *</Label>
                                        <Select 
                                            value={newSong.label_id} 
                                            onValueChange={(val) => setNewSong({...newSong, label_id: val})}
                                        >
                                            <SelectTrigger className="bg-background/50" data-testid="label-select">
                                                <SelectValue placeholder="Select label" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {labels.map((label) => (
                                                    <SelectItem key={label.id} value={label.id}>
                                                        {label.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>UPC Code *</Label>
                                        <Input
                                            value={newSong.upc_code}
                                            onChange={(e) => setNewSong({...newSong, upc_code: e.target.value})}
                                            className="bg-background/50 font-mono"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>ISRC Code *</Label>
                                        <Input
                                            value={newSong.isrc_code}
                                            onChange={(e) => setNewSong({...newSong, isrc_code: e.target.value})}
                                            className="bg-background/50 font-mono"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Audio File</Label>
                                        <Input
                                            value={newSong.audio_file}
                                            onChange={(e) => setNewSong({...newSong, audio_file: e.target.value})}
                                            className="bg-background/50"
                                            placeholder="File path or URL"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Artwork File</Label>
                                        <Input
                                            value={newSong.artwork_file}
                                            onChange={(e) => setNewSong({...newSong, artwork_file: e.target.value})}
                                            className="bg-background/50"
                                            placeholder="File path or URL"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-song-btn">
                                        Add Song
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Catalog Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Songs</p>
                                <p className="text-3xl font-bold text-primary">
                                    {catalogSummary?.total_songs?.toLocaleString() || 0}
                                </p>
                            </div>
                            <div className="p-3 bg-primary/10 rounded-full">
                                <Music className="w-6 h-6 text-primary" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total Labels</p>
                                <p className="text-3xl font-bold text-secondary">
                                    {catalogSummary?.total_labels?.toLocaleString() || 0}
                                </p>
                            </div>
                            <div className="p-3 bg-secondary/10 rounded-full">
                                <Users className="w-6 h-6 text-secondary" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border-cyan-500/20">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Recent Uploads (7d)</p>
                                <p className="text-3xl font-bold text-cyan-500">
                                    {catalogSummary?.recent_uploads?.toLocaleString() || 0}
                                </p>
                            </div>
                            <div className="p-3 bg-cyan-500/10 rounded-full">
                                <TrendingUp className="w-6 h-6 text-cyan-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search Section */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Search className="w-5 h-5 text-primary" />
                        Search Catalog
                    </CardTitle>
                    <CardDescription>Search songs by name, artist, ISRC, UPC or search labels by name</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search songs, labels, artists, ISRC, UPC..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-background/50"
                                data-testid="search-input"
                            />
                            {searchQuery && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                                    onClick={clearSearch}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                        <Button type="submit" disabled={searching || !searchQuery.trim()}>
                            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                        </Button>
                    </form>

                    {/* Search Results with Tabs */}
                    {showSearchResults && (
                        <div className="mt-4">
                            <Tabs value={searchTab} onValueChange={setSearchTab}>
                                <div className="flex items-center justify-between mb-3">
                                    <TabsList className="bg-muted/30">
                                        <TabsTrigger value="songs" className="flex items-center gap-2">
                                            <Music className="w-4 h-4" />
                                            Songs ({searchResults.length})
                                        </TabsTrigger>
                                        <TabsTrigger value="labels" className="flex items-center gap-2">
                                            <Tag className="w-4 h-4" />
                                            Labels ({matchingLabels.length})
                                        </TabsTrigger>
                                    </TabsList>
                                    <Button variant="ghost" size="sm" onClick={clearSearch}>
                                        Clear Results
                                    </Button>
                                </div>

                                <TabsContent value="songs" className="mt-0">
                                    {searchResults.length > 0 ? (
                                        <div className="rounded-lg border border-border/40 overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-muted/20 hover:bg-muted/20">
                                                        <TableHead className="font-mono text-xs uppercase">Song Name</TableHead>
                                                        <TableHead className="font-mono text-xs uppercase">Singer</TableHead>
                                                        <TableHead className="font-mono text-xs uppercase">Label</TableHead>
                                                        <TableHead className="font-mono text-xs uppercase">UPC</TableHead>
                                                        <TableHead className="font-mono text-xs uppercase">ISRC</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {searchResults.map((song) => (
                                                        <TableRow key={song.id} className="hover:bg-muted/10 border-border/40">
                                                            <TableCell className="font-medium">{song.song_name}</TableCell>
                                                            <TableCell className="text-muted-foreground">{song.singer_name || '-'}</TableCell>
                                                            <TableCell>
                                                                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                                                    {song.label_name}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="font-mono text-sm text-muted-foreground">
                                                                {song.upc_code || '-'}
                                                            </TableCell>
                                                            <TableCell className="font-mono text-sm text-muted-foreground">
                                                                {song.isrc_code || '-'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No songs found matching "{searchQuery}"
                                        </div>
                                    )}
                                </TabsContent>

                                <TabsContent value="labels" className="mt-0">
                                    {matchingLabels.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {matchingLabels.map((label) => (
                                                <div 
                                                    key={label.label_id} 
                                                    className="p-4 bg-muted/20 rounded-lg border border-border/40 hover:border-primary/40 transition-colors cursor-pointer group"
                                                    onClick={() => handleViewLabelSongs(label)}
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium truncate group-hover:text-primary transition-colors" title={label.label_name}>
                                                                {label.label_name}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground font-mono">
                                                                {label.label_code}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-3">
                                                        <div className="flex items-center gap-1 text-muted-foreground">
                                                            <Music className="w-4 h-4" />
                                                            <span className="text-lg font-bold text-foreground">
                                                                {label.song_count}
                                                            </span>
                                                            <span className="text-xs">songs</span>
                                                        </div>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            className="h-7 px-2"
                                                        >
                                                            <Eye className="w-3 h-3 mr-1" />
                                                            View
                                                        </Button>
                                                    </div>
                                                    <div className="mt-2 text-xs text-secondary">
                                                        {label.revenue_share}% revenue share
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground">
                                            No labels found matching "{searchQuery}"
                                        </div>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Top Labels by Catalog Size */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Disc3 className="w-5 h-5 text-primary" />
                                Catalog by Label
                            </CardTitle>
                            <CardDescription>Click on a label to view its songs, or use search above to find specific labels</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {catalogSummary?.labels_breakdown?.slice(0, 20).map((label, index) => (
                            <div 
                                key={label.label_id} 
                                className="p-4 bg-muted/20 rounded-lg border border-border/40 hover:border-primary/40 transition-colors cursor-pointer group"
                                onClick={() => handleViewLabelSongs(label)}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate group-hover:text-primary transition-colors" title={label.label_name}>
                                            {label.label_name}
                                        </p>
                                        <p className="text-xs text-muted-foreground font-mono">
                                            {label.label_code}
                                        </p>
                                    </div>
                                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full ml-2">
                                        #{index + 1}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mt-3">
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                        <Music className="w-4 h-4" />
                                        <span className="text-lg font-bold text-foreground">
                                            {label.song_count}
                                        </span>
                                        <span className="text-xs">songs</span>
                                    </div>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewLabelSongs(label);
                                        }}
                                    >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View
                                    </Button>
                                </div>
                                <div className="mt-2 text-xs text-secondary">
                                    {label.revenue_share}% revenue share
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    {catalogSummary?.labels_breakdown?.length > 20 && (
                        <p className="text-center text-sm text-muted-foreground mt-4">
                            Showing top 20 of {catalogSummary.labels_breakdown.length} labels. Use search above to find any label.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default ContentPage;
